import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type SheetType = 'menu' | 'tooltip' | 'contact-us' | 'actions' | 'qr-code' | 'share-link';

export type TooltipData = {
  title: string;
  description: string;
};

export type QrCodeData = {
  link: string;
};

// todo fix type
export type Sheet = {
  id: string;
  type: SheetType;
  arguments?: unknown;
};

export type SheetHeight = {
  id: string;
  height: number | undefined;
};

type ContextValue = {
  sheets: Array<Sheet>;
  getIsSheetOpen: (type: SheetType) => string | null;
  openMenu: () => void;
  openContactUs: () => void;
  openTooltip: (data: TooltipData) => void;
  openActions: () => void;
  openQrCode: (data: QrCodeData) => void;
  openShareLink: () => void;
  close: (id: string) => void;
  minimalSheetHeight: number | undefined;
  setSheetHeight: (id: string, value: number) => void;
};

const SheetRouterContext = createContext<ContextValue>({
  sheets: [],
  getIsSheetOpen: (_type) => null,
  openMenu: () => {},
  openContactUs: () => {},
  openTooltip: () => {},
  openActions: () => {},
  openQrCode: () => {},
  openShareLink: () => {},
  close: (_id) => {},
  minimalSheetHeight: undefined,
  setSheetHeight: (_id, _value) => {},
});

type Props = {
  children: React.ReactNode;
};

export const SheetRouterProvider = ({ children }: Props) => {
  const [sheets, setSheets] = useState<Array<Sheet>>([]);
  const [sheetHeights, setSheetHeights] = useState<Array<SheetHeight>>([]);

  const openSheet = useCallback((sheet: Sheet) => {
    setSheets((sheets) => [...sheets, sheet]);
    setSheetHeights((sheetHeights) => [...sheetHeights, { id: sheet.id, height: undefined }]);
  }, []);

  const openMenu = useCallback(() => {
    openSheet({
      id: crypto.randomUUID(),
      type: 'menu',
    });
  }, [openSheet]);

  const openContactUs = useCallback(() => {
    openSheet({
      id: crypto.randomUUID(),
      type: 'contact-us',
    });
  }, [openSheet]);

  const openTooltip = useCallback(
    (data: TooltipData) => {
      openSheet({
        id: crypto.randomUUID(),
        type: 'tooltip',
        arguments: data,
      });
    },
    [openSheet],
  );

  const openQrCode = useCallback(
    (data: QrCodeData) => {
      openSheet({
        id: crypto.randomUUID(),
        type: 'qr-code',
        arguments: data,
      });
    },
    [openSheet],
  );

  const openActions = useCallback(() => {
    openSheet({
      id: crypto.randomUUID(),
      type: 'actions',
    });
  }, [openSheet]);

  const openShareLink = useCallback(() => {
    openSheet({
      id: crypto.randomUUID(),
      type: 'share-link',
    });
  }, [openSheet]);

  const close = useCallback((sheetId: string) => {
    setSheets((sheets) => sheets.filter(({ id }) => id !== sheetId));
    setSheetHeights((sheets) => sheets.filter(({ id }) => id !== sheetId));
  }, []);

  const setSheetHeight = useCallback((sheetId: string, value: number) => {
    setSheetHeights((sheets) =>
      sheets.map(({ id, height }) => {
        if (id === sheetId) {
          return { id, height: value };
        }
        return { id, height };
      }),
    );
  }, []);

  const getIsSheetOpen = useCallback(
    (type: SheetType) => {
      return sheets.find((sheet) => sheet.type === type)?.id ?? null;
    },
    [sheets],
  );

  const minimalSheetHeight = useMemo(() => {
    return sheetHeights.at(-1)?.height;
  }, [sheetHeights]);

  const value: ContextValue = useMemo(() => {
    return {
      getIsSheetOpen,
      sheets,
      openMenu,
      openTooltip,
      openContactUs,
      openActions,
      openQrCode,
      openShareLink,
      close,
      minimalSheetHeight,
      setSheetHeight,
    };
  }, [
    getIsSheetOpen,
    sheets,
    openMenu,
    openTooltip,
    openQrCode,
    openContactUs,
    openActions,
    openShareLink,
    close,
    minimalSheetHeight,
    setSheetHeight,
  ]);

  return <SheetRouterContext.Provider value={value}>{children}</SheetRouterContext.Provider>;
};

export const useSheetRouter = () => {
  return useContext(SheetRouterContext);
};
